import { CommonModule, NgTemplateOutlet, NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, EventEmitter, inject, Inject, input, OnDestroy, Output, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from 'auth';
import { I18nService, TranslatePipe, TranslateService } from 'i18n';

import {
  ColorModeService,
  ContainerComponent,
  DropdownComponent,
  DropdownItemDirective,
  DropdownMenuDirective,
  DropdownToggleDirective,
  HeaderComponent,
  HeaderNavComponent,
  HeaderTogglerDirective,
  SidebarToggleDirective
} from '@coreui/angular-pro';

import { HttpClient } from '@angular/common/http';
import { IconDirective } from '@coreui/icons-angular';
import { ENVIRONMENT, Logger, Platform } from 'shared';
import { BreadcrumbComponent } from '../../../../components/breadcrumb.component';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NotificationsService } from '../../../../services/notifications.service';
import { HEADER_ACTION_COMPONENT } from '../../../../services/header-action.token';
import { AppNotification } from '../../../../models/notification.models';

@Component({
  selector: 'app-default-header',
  styleUrls: ['./default-header.component.scss'],
  templateUrl: './default-header.component.html',
  imports: [ContainerComponent, HeaderTogglerDirective, SidebarToggleDirective,
            IconDirective, HeaderNavComponent, NgTemplateOutlet, NgComponentOutlet,
            /*BreadcrumbRouterComponent,*/ BreadcrumbComponent,
            DropdownComponent, DropdownToggleDirective, DropdownMenuDirective,
            DropdownItemDirective, CommonModule, TranslatePipe, RouterLink ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DefaultHeaderComponent extends HeaderComponent implements OnDestroy {
  protected logger = new Logger('DefaultHeaderComponent');
  @Output() openUserEditModal = new EventEmitter<void>();

  /** App-provided header action (HEADER_ACTION_COMPONENT), rendered before the bell. */
  readonly headerAction = inject(HEADER_ACTION_COMPONENT, { optional: true });

  readonly #colorModeService = inject(ColorModeService);
  readonly colorMode = this.#colorModeService.colorMode;

  // --- Notification bell -------------------------------------------------------
  // Lives here (not in `community`) because the header must not import that lib.
  // Seeded from the bootstrap user, then kept fresh by NotificationsService's
  // own 60s poll for as long as this header (i.e. the app) is alive.
  private readonly notificationsService = inject(NotificationsService);
  private readonly router = inject(Router);
  private readonly user = toSignal(this.authService.getUser$(), { initialValue: null });
  /** Only rendered when the backend says the viewer may enter the Community; anything but an explicit false is enabled. */
  readonly communityEnabled = computed(() => this.user()?.community_enabled !== false);
  readonly unreadNotifications = this.notificationsService.unreadCount;
  readonly recentNotifications = signal<AppNotification[]>([]);
  readonly notificationsLoading = signal(false);

  badge = input<boolean>(false);

  readonly colorModes = [
    { name: 'light', text: 'Light', icon: 'cilSun' },
    { name: 'dark', text: 'Dark', icon: 'cilMoon' },
    { name: 'auto', text: 'Auto', icon: 'cilContrast' }
  ];

  readonly icons = computed(() => {
    const currentMode = this.colorMode();
    return this.colorModes.find(mode => mode.name === currentMode)?.icon ?? 'cilSun';
  });

  // readonly showExtras = environment.VIEW_EXTRAS === 'true';

  availableLanguages: string[] = [];
  selectedLanguage = signal(this.i18nService.getLanguage());

  // Endonyms, not English names: a Russian speaker scanning the picker looks for
  // "Русский", not "Russian". Keyed by the exact code the backend hands out in
  // the domain's `languages` setting, so es-MX needs its own entry - the picker
  // does no prefix matching and would otherwise render a blank label.
  readonly languageLabels: Record<string, string> = {
    en: "English",
    de: "Deutsch",
    es: "Español",
    "es-MX": "Español (México)",
    fr: "Français",
    pt: "Português",
    it: "Italiano",
    ru: "Русский",
    cs: "Čeština",
    sk: "Slovenčina",
    ar: "العربية"
  };

  // flag icons are not displayed by Chrome on Windows!
  // Unlike regular emojis which are one code point flags are a sequence e.g. 🇬🇧 = 🇬 + 🇧 = U+1F1EC + U+1F1E7
  // operating system should recognize the characters and replace them with a single flag glyph which does not happen on Windows + Chrome
  // For universal solution we should go with SVG/PNG flags
  // readonly languageLabels: Record<string, string> = {
  //   en: "🇬🇧 English",
  //   de: "🇩🇪 Deutsch",
  //   es: "🇪🇸 Español",
  //   fr: "🇫🇷 Français",
  //   pt: "🇵🇹 Português",
  //   it: "🇮🇹 Italiano"
  // };

  readonly flags: Partial<Record<string, string>> = {
    en: 'GB',
    'es-MX': 'MX'
  };

  constructor(
    private i18nService: I18nService,
    private authService: AuthService,
    private http: HttpClient,
    public platform: Platform,
    public translateService: TranslateService,
    private destroyRef: DestroyRef,
    @Inject(ENVIRONMENT) public environment: any
  ) {
    super();
    this.availableLanguages = translateService.getAvailableLangs();
    // this.selectedLanguage = this.i18nService.getLanguage();
    effect(() => {
      const language = this.selectedLanguage();
      if (language!=this.i18nService.getLanguage()) {
        this.i18nService.setLanguage(language);
      }
    })
    // Seed the bell from the bootstrap/login payload; the poll below then
    // keeps it current without waiting a full minute for the first answer.
    effect(() => this.notificationsService.seed(this.user()?.unread_notifications));
    this.notificationsService.start();
  }

  ngOnDestroy(): void {
    this.notificationsService.stop();
  }

  /** Loaded lazily when the bell dropdown is opened (avoids a request on every page for users who never check it). */
  loadRecentNotifications(): void {
    this.notificationsLoading.set(true);
    this.notificationsService.list({ perPage: 8 }).subscribe({
      next: res => {
        this.recentNotifications.set(res.data);
        this.notificationsLoading.set(false);
      },
      error: () => this.notificationsLoading.set(false)
    });
  }

  /** Mark read (if needed) and navigate to the notification's target. */
  openNotification(n: AppNotification): void {
    if (!n.read) {
      this.notificationsService.markRead(n.id).subscribe({
        next: () => this.notificationsService.refresh(),
        error: () => {}
      });
      n.read = true;
    }
    if (n.url) this.router.navigateByUrl(`/${this.getLanguage()}${n.url}`);
  }

  markAllNotificationsRead(): void {
    this.notificationsService.markAllRead().subscribe({
      next: () => {
        this.recentNotifications.update(list => list.map(n => ({ ...n, read: true })));
        this.notificationsService.unreadCount.set(0);
      },
      error: () => {}
    });
  }

  notificationsLink(): string {
    return `/${this.getLanguage()}/community/notifications`;
  }

  /** Straight to the per-kind switches — the bell is where someone stands when
   *  they decide they get too much of something. */
  notificationSettingsLink(): string {
    return `/${this.getLanguage()}/community/settings`;
  }

  getUsername() {
    return this.authService.getUser()?.username;
  }

  getLanguage() {
    return this.i18nService.getLanguage();
  }

  openUserEditModalx() {
    this.openUserEditModal.emit();
  }
  
  sidebarId = input('mainMenuSidebar');

  public newMessages = [];
  public newNotifications = [];
  public newStatus = [];
  public newTasks = [];
  
  changeLanguage(language: string) {
    // Apply locale in the UI immediately; persist for signed-in users in the background.
    // Wolfpack (and any API without this route) previously blocked switching because the POST failed.
    this.selectedLanguage.set(language);
    this.http.post<{ message?: string }>('/user/update_language', { language })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {},
        error: (err) => this.logger.warn('Could not persist language preference', err)
      });
  }

  logout() {
    this.authService.logout()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.logger.log('Logout successful'),
        error: (error) => this.logger.error('Logout error:', error)
      });
  }
}
